import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { useLang, errorText } from "@/lib/i18n";
import { fmtDate, guestsLabel, nightsBetween, nightsLabel, nok } from "@/lib/format";

/** Smooth count-up for money amounts */
export const AnimatedAmount = ({ value, lang, className = "", testid }) => {
  const [display, setDisplay] = useState(value ?? 0);
  const fromRef = useRef(value ?? 0);
  useEffect(() => {
    const to = value ?? 0;
    const from = fromRef.current;
    if (from === to) return undefined;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setDisplay(to); fromRef.current = to; return undefined; }
    const start = performance.now();
    const dur = 400;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * e));
      if (p < 1) raf = requestAnimationFrame(tick); else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className={className} data-testid={testid}>{nok(display, lang)}</span>;
};

export const NightlyBreakdown = ({ quote, defaultOpen = false }) => {
  const { lang, tx } = useLang();
  const [open, setOpen] = useState(defaultOpen);
  if (!quote) return null;
  const byDate = {};
  quote.rooms.forEach((r) => r.nights.forEach((n) => { byDate[n.date] = byDate[n.date] || { date: n.date, weekend: n.weekend, price: 0 }; byDate[n.date].price += n.price; }));
  const nights = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  return (
    <div data-testid="booking-summary-nightly-breakdown">
      <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-sm font-medium text-foreground underline underline-offset-4" aria-expanded={open} data-testid="booking-summary-breakdown-toggle">
        {tx("Vis natt for natt", "Show night by night")}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="mt-2 space-y-1.5">
          {nights.map((n) => (
            <li key={n.date} className="flex items-center justify-between text-sm" data-testid="booking-summary-night-row" data-date={n.date}>
              <span className="text-muted-foreground">{fmtDate(n.date, lang, "EEE d. MMM")}{n.weekend && <span className="ml-1.5 text-xs">({tx("helg", "weekend")})</span>}</span>
              <span className="tnum">{nok(n.price, lang)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Row = ({ label, value, action, onAction, testid }) => (
  <div className="flex items-start justify-between gap-4 text-sm" data-testid={testid}>
    <div className="min-w-0">
      <p className="font-medium text-foreground">{label}</p>
      <p className="text-muted-foreground tabular">{value}</p>
    </div>
    {action && <button type="button" onClick={onAction} className="shrink-0 text-sm font-semibold text-foreground underline underline-offset-4" data-testid={testid ? `${testid}-edit` : undefined}>{action}</button>}
  </div>
);

/**
 * The receipt body. Shared by the desktop sidebar and the mobile drawer.
 */
export const SummaryBody = ({ ci, co, adults, children, dog, cart, quote, byId, isLoading, step, cta, image, onEdit, layout = "drawer" }) => {
  const { lang, tx, tr } = useLang();
  const n = nightsBetween(ci, co);
  const party = adults + children;
  const hasDates = !!(ci && co);
  const first = cart.length ? byId[cart[0].room_type_id] : null;
  const title = first ? `${tr(first.name)}${cart.length > 1 ? ` + ${cart.length - 1} ${tx(cart.length - 1 === 1 ? "rom til" : "rom til", cart.length - 1 === 1 ? "more room" : "more rooms")}` : ""}` : tx("Opphaldet ditt", "Your stay");
  const guestsText = `${adults} ${adults === 1 ? tx("vaksen", "adult") : tx("vaksne", "adults")}${children > 0 ? `, ${children} ${children === 1 ? tx("barn", "child") : tx("born", "children")}` : ""}${dog ? ` · ${tx("hund", "dog")}` : ""}`;
  const card = layout === "card";
  return (
    <div data-testid="booking-summary">
      {card ? (
        <div className="relative -mx-6 -mt-6 mb-5 h-44 overflow-hidden rounded-t-[24px] bg-muted">
          <img key={image} src={image} alt="" className="h-full w-full object-cover" />
          <div className="scrim-bottom absolute inset-x-0 bottom-0 h-3/4" aria-hidden />
          <div className="absolute inset-x-5 bottom-4 text-white">
            <p className="text-xs font-medium text-white/75">Fitjar Fjordhotell</p>
            <p className="mt-0.5 truncate text-lg font-bold leading-snug tracking-tight" data-testid="booking-summary-title">{title}</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <img key={image} src={image} alt="" className="h-[72px] w-24 shrink-0 rounded-xl object-cover" />
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Fitjar Fjordhotell</p>
            <p className="mt-0.5 truncate text-[0.9375rem] font-semibold leading-snug text-foreground" data-testid="booking-summary-title">{title}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{tx("Frukost inkludert · betal på hotellet", "Breakfast included · pay at the hotel")}</p>
          </div>
        </div>
      )}

      {!card && <div className="my-5 h-px bg-border" />}
      <h3 className="text-base font-semibold text-foreground">{tx("Detaljar", "Details")}</h3>
      <div className="mt-3 space-y-3">
        <Row label={tx("Datoar", "Dates")} value={hasDates ? `${fmtDate(ci, lang)} – ${fmtDate(co, lang)} · ${nightsLabel(n, lang)}` : tx("Ikkje valt enno", "Not chosen yet")} action={step > 1 ? tx("Endre", "Edit") : undefined} onAction={onEdit} testid="booking-summary-dates" />
        <Row label={tx("Gjester", "Guests")} value={guestsText} action={step > 1 ? tx("Endre", "Edit") : undefined} onAction={onEdit} testid="booking-summary-guests" />
      </div>

      <div className="my-5 h-px bg-border" />
      <h3 className="text-base font-semibold text-foreground">{tx("Pris", "Price")}</h3>
      <div className="mt-3">
        {!hasDates || cart.length === 0 ? (
          <p className="text-sm leading-relaxed text-muted-foreground" data-testid="booking-summary-empty">{!hasDates ? tx("Vel innsjekk og utsjekk for å sjå pris.", "Choose check-in and check-out to see the price.") : tx("Legg til eit rom for å sjå pris.", "Add a room to see the price.")}</p>
        ) : isLoading && !quote ? (
          <div className="space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-1/2" /><Skeleton className="h-7 w-full" /></div>
        ) : quote ? (
          <div className="space-y-3">
            <ul className="space-y-2" data-testid="booking-summary-rooms">
              {quote.rooms.map((r, i) => (
                <li key={i} className="flex items-start justify-between gap-4 text-sm">
                  <span className="min-w-0 text-muted-foreground">
                    {tr(r.name)} · {guestsLabel(r.guests, lang)}{r.extra_beds > 0 && ` · ${r.extra_beds} ${tx("ekstraseng", "extra bed")}`}{r.board !== "standard" && r.board_name && ` · ${tr(r.board_name)}`}
                  </span>
                  <span className="shrink-0 tnum text-foreground">{nok(r.subtotal, lang)}</span>
                </li>
              ))}
            </ul>
            {quote.problems?.length > 0 && (
              <ul className="space-y-1 rounded-lg bg-secondary px-3 py-2 text-xs text-warning" data-testid="booking-summary-problems">
                {quote.problems.map((p, i) => <li key={i}>{errorText(p.code, lang)}{p.room_type_id && byId[p.room_type_id] ? ` (${tr(byId[p.room_type_id].name)})` : ""}</li>)}
              </ul>
            )}
            <NightlyBreakdown quote={quote} />
            <div className="h-px bg-border" />
            <div className="flex items-baseline justify-between">
              <span className="text-base font-semibold text-foreground">{tx("Totalt", "Total")} <span className="text-sm font-normal text-muted-foreground">(NOK)</span></span>
              <AnimatedAmount value={quote.total} lang={lang} className="text-xl font-semibold tnum text-foreground" testid="booking-summary-total" />
            </div>
            <p className="text-xs text-muted-foreground">{guestsLabel(quote.guests, lang)} · {tx("frukost og mva inkludert", "breakfast and VAT included")}</p>
            {step < 4 && party > 0 && quote.guests < party && (
              <p className="rounded-lg bg-secondary px-3 py-2 text-xs text-warning" data-testid="booking-summary-capacity-warning">{tx(`Romma har plass til ${quote.guests} av ${party} gjester. Legg til eit rom til, eller juster gjester.`, `Rooms sleep ${quote.guests} of ${party} guests. Add another room or adjust guests.`)}</p>
            )}
          </div>
        ) : <p className="text-sm text-muted-foreground">–</p>}
      </div>

      {cta && (
        <div className="mt-6">
          <Button variant="brand" size="xl" className="w-full rounded-2xl" onClick={cta.onClick} disabled={cta.disabled} type={cta.type || "button"} form={cta.form} data-testid={cta.testid || "booking-receipt-cta"}>{cta.label}</Button>
          {cta.hint && <p className="mt-2.5 text-center text-xs text-muted-foreground">{cta.hint}</p>}
        </div>
      )}

      <ul className="mt-5 space-y-2 text-xs text-muted-foreground">
        <li className="flex gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-success" /> {tx("Betal på hotellet – inga forskotsbetaling, ingen kortdata.", "Pay at the hotel – no prepayment, no card details.")}</li>
        <li className="flex gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-success" /> {quote?.cancel_deadline_display ? tx(`Gratis avbestilling fram til ${quote.cancel_deadline_display.nn}.`, `Free cancellation until ${quote.cancel_deadline_display.en}.`) : tx("Gratis avbestilling fram til kl. 18 dagen før.", "Free cancellation until 18:00 the day before.")}</li>
        <li className="flex gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-success" /> {tx("Beste pris når du bestiller direkte.", "Best rate when you book direct.")}</li>
      </ul>
    </div>
  );
};

export const DesktopSummary = (props) => (
  <aside className="hidden lg:block">
    <div className="sticky top-[96px] rounded-[24px] border border-border bg-card p-6 shadow-float" data-testid="booking-summary-sidebar">
      <SummaryBody {...props} layout="card" />
    </div>
  </aside>
);

/** Mobile bottom bar: total + open receipt drawer + step CTA */
export const MobileSummaryBar = ({ cta, quote, ...props }) => {
  const { lang, tx } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden no-print" data-testid="booking-mobile-bar">
        <div className="flex items-center justify-between gap-4">
          <button type="button" onClick={() => setOpen(true)} className="min-w-0 flex-1 text-left" data-testid="booking-summary-open">
            {quote ? <AnimatedAmount value={quote.total} lang={lang} className="block text-lg font-semibold leading-tight tnum text-foreground" testid="booking-mobile-total" /> : <span className="block text-lg font-semibold leading-tight text-foreground" data-testid="booking-mobile-total">{tx("Di bestilling", "Your booking")}</span>}
            <span className="block text-sm text-muted-foreground underline underline-offset-2">{tx("Vis detaljar", "Show details")}</span>
          </button>
          {cta && (
            <Button variant="brand" size="lg" className="px-6" onClick={cta.onClick} disabled={cta.disabled} data-testid="booking-mobile-cta" type={cta.type || "button"} form={cta.form}>
              {cta.label}
            </Button>
          )}
        </div>
      </div>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="max-h-[92dvh] rounded-t-2xl border-0 bg-background" data-testid="booking-summary-drawer">
          <div className="overflow-y-auto px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-between py-2">
              <div>
                <DrawerTitle className="text-lg font-semibold text-foreground">{tx("Di bestilling", "Your booking")}</DrawerTitle>
                <DrawerDescription className="sr-only">{tx("Samandrag av bestillinga", "Booking summary")}</DrawerDescription>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card" aria-label={tx("Lukk", "Close")} data-testid="booking-summary-drawer-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="pt-3"><SummaryBody quote={quote} {...props} /></div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
